import React, { useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import { maybeUseValue } from '@shared/utils/functions';
import type { CardProps, CardElevation, CardContext, CardContentProps, CardMediaProps, CardActionProps } from './';

import {
  StyledCardContentRoot,
  StyledCardActionsRoot,
  StyledCardMediaWrapper,
  StyledCardMediaRoot,
  StyledCardRoot,
  getCardStyles,
  getCardActionsStyles,
  getCardMediaStyles
} from './Card.styles';
import { CONTAINER_INSET_RULES, ELEVATION_LIGHTING } from './Card.constants';
import { getOverrides, useOverrides } from '@shared/utils/overrides';
import { animationTransition } from '@shared/utils/animationTransition';

const [CardProvider, useCardContext] = createContext<CardContext>({ name: 'Card' });

const CardContent: React.FC<CardContentProps> = props => {
  const { align = 'left', children, className, disableBackground = false, inset = 'md', overrides = {}, size = 'full' } = props;

  const [Root, rootProps] = getOverrides(overrides.Root, StyledCardContentRoot);
  return (
    <Root
      $align={align}
      $size={size}
      className={className}
      backgroundColor={maybeUseValue(!disableBackground, `rgba(255, 255, 255, 0.9)`)}
      _groupHover={maybeUseValue(!disableBackground, { backgroundColor: `rgba(255, 255, 255, 0.95)` })}
      transition={maybeUseValue(!disableBackground, animationTransition('background-color'))}
      {...CONTAINER_INSET_RULES[inset]}
      {...rootProps}
    >
      <div>{children}</div>
    </Root>
  );
};

CardContent.displayName = 'CardContent';

//////////

const CardActions: React.FC<CardActionProps> = props => {
  const { children, className, overrides = {} } = props;
  const [Root, rootProps] = getOverrides(overrides.Root, StyledCardActionsRoot);
  const { elevation } = useCardContext();
  const styles = getCardActionsStyles({ elevation });
  return (
    <Root className={className} {...styles.Root} {...rootProps}>
      {children}
    </Root>
  );
};

CardActions.displayName = 'CardActions';

//////////

const cardMediaDefaultComponents = {
  Root: StyledCardMediaRoot,
  Wrapper: StyledCardMediaWrapper
} as const;

const CardMedia: React.FC<CardMediaProps> = props => {
  const {
    children,
    layer = 'background',
    enableWindowPeek = false,
    foregroundHeight,
    overrides = {},
    placement = 'center',
    src,
    windowHeight = 116,
    scaleFrom = 1,
    scaleTo = 1.05,
    ...restProps
  } = props;

  const { elevation } = useCardContext();

  const {
    Root: [Root, rootProps],
    Wrapper: [Wrapper, bgProps]
  } = useOverrides(cardMediaDefaultComponents, overrides);

  const styles = getCardMediaStyles({ src, layer, placement, foregroundHeight });

  return (
    <Wrapper $enableWindowPeek={enableWindowPeek} $windowHeight={windowHeight} {...bgProps} {...restProps}>
      <Root
        className="card__media"
        transitionTimingFunction={`cubic-bezier(0.4, 0, 0.2, 1)`}
        transitionDuration="500ms"
        transitionDelay="20ms"
        transitionProperty="transform"
        // It is necessary to apply translate3d to prevent SVG flickering
        // https://maximelafarie.com/avoid-css-flickering
        transform={`scale(${scaleFrom}) translate3d(0, 0, 0)`}
        _groupHover={{
          transform: `scale(${elevation === 0 ? scaleFrom : scaleTo}) translate3d(0, 0, 0)`
        }}
        {...styles.Root}
        {...rootProps}
      />
    </Wrapper>
  );
};

CardMedia.displayName = 'CardMedia';

//////////

const Card: React.FC<CardProps> = props => {
  const { children, backgroundColor, elevation, overrides = {}, style, ...htmlProps } = props as CardProps & typeof cardDefaultProps;

  const [Root, rootProps] = getOverrides(overrides.Root, StyledCardRoot);

  const isInteractive = !!props.onClick;

  const contextValue = useMemo(() => {
    return {
      elevation,
      isInteractive
    };
  }, [elevation, isInteractive]);

  const shadowStyles = ELEVATION_LIGHTING[contextValue.elevation];

  const styles = getCardStyles({ backgroundColor, isInteractive: contextValue.isInteractive });

  return (
    <CardProvider value={contextValue}>
      <Root
        as={'section'}
        $applyGridRules={!rootProps.display}
        role="group"
        $style={style}
        __css={{
          ...styles.Root,
          boxShadow: shadowStyles.default,
          transitionTimingFunction: `cubic-bezier(0.4, 0, 0.2, 1)`,
          transitionDuration: '400ms',
          transitionProperty: 'transform, box-shadow',
          transitionDelay: '0ms, 50ms',
          transform: [null, null, null, `scale(1) translate3d(0, 0, 0)`],
          ...(contextValue.elevation === 1
            ? {
                _hover: {
                  boxShadow: shadowStyles.hover,
                  transitionDelay: '0ms, 50ms',
                  transform: [null, null, null, 'scale(1.05) translate3d(0, 0, 0)']
                },
                _active: {
                  boxShadow: shadowStyles.active,
                  transform: 'scale(1.02) translate3d(0, 0, 0)'
                }
              }
            : {})
        }}
        {...htmlProps}
        {...rootProps}
      >
        {children}
      </Root>
    </CardProvider>
  );
};

const cardDefaultProps = {
  backgroundColor: '#fff',
  elevation: 1 as CardElevation,
  style: {}
} as const;

Card.displayName = 'Card';
Card.defaultProps = cardDefaultProps;

export { Card, CardContent, CardMedia, CardActions, useCardContext };
